import mergeRefs from '@atlaskit/ds-lib/merge-refs';
import Textfield from '@atlaskit/textfield';
import ValidationStatus from '@databases/form/ValidationStatus';
import { Metrics } from '@databases/frontend-metrics';
import { useTypingPerformance } from '@databases/frontend-metrics/useTypingPerformance';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
  useRef,
} from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

/** Number of max characters a database name can have. */
export const DATABASE_NAME_MAX_LENGTH = 60;

/**
 * Returns true if the database name has less than or equal to x (see {@link DATABASE_NAME_MAX_LENGTH}) characters.
 */
export function validateDatabaseName(name: string): boolean {
  return name.length <= DATABASE_NAME_MAX_LENGTH;
}

export const DatabaseNameEditContainer = styled.div`
  position: relative;
`;

export interface DatabaseNameEditProps {
  contentId: string;
  showPlaceholder?: boolean;
  onChange?: (name: string, isValid?: boolean) => void;
}

const DatabaseNameEdit: FunctionComponent<DatabaseNameEditProps> = ({
  contentId,
  showPlaceholder = false,
  onChange = () => {},
}) => {
  const { formatMessage } = useIntl();
  const ref = useRef<HTMLInputElement | null>(null);

  const value = defaultStoreUtils.useEqualSelector((state) => {
    const database = selectDatabaseByContentIdRequired(state, { contentId });
    return database.name;
  });
  const [name, setName] = useState(value);
  const [isValidName, setIsValidName] = useState(true);

  const handleChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      setName(e.currentTarget.value);
      const isValid = validateDatabaseName(e.currentTarget.value);
      onChange(e.currentTarget.value, isValid);
      setIsValidName(isValid);
    },
    [onChange]
  );

  const typingPerformanceRef = useTypingPerformance(Metrics.MEAN_DATABASE_NAME_TYPING);

  useEffect(() => {
    if (ref) {
      ref?.current?.focus();
    }
  }, [ref]);

  return (
    <ValidationStatus
      error={
        isValidName
          ? undefined
          : formatMessage(messages['validation.database-name-length'], {
              maxLength: DATABASE_NAME_MAX_LENGTH,
            })
      }
    >
      {(props) => (
        <DatabaseNameEditContainer>
          <Textfield
            {...props}
            autoComplete="off"
            placeholder={
              showPlaceholder
                ? formatMessage(messages['unnamed-entities.untitled-database'])
                : undefined
            }
            value={name}
            onChange={handleChange}
            ref={mergeRefs([typingPerformanceRef, ref])}
            size={1} // this resets the min width for the input field
            testId={'database-name-edit'}
          />
        </DatabaseNameEditContainer>
      )}
    </ValidationStatus>
  );
};

export default DatabaseNameEdit;
